import { useEffect, useState } from 'react';
import { ChevronLeft, Trash2, Plus } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { admin, palette, radii, typography } from '../../../styles/tokens';
import { useFieldErrors, type FieldErrors } from '../hooks/useFieldErrors';
import { canonicalMoney, isPositive } from '../../../lib/money';

interface ServicesItemProps {
  businessId: string;
  onBack: () => void;
  onSaved: () => void;
}

interface ServiceRow {
  id: string;
  isNew: boolean;
  dirty: boolean;
  deleted: boolean;
  name: string;
  price: string;
  duration_minutes: string;
  description: string;
}

function tempId(n: number) {
  return `new-${n}-${Date.now()}`;
}

export default function ServicesItem({ businessId, onBack, onSaved }: ServicesItemProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ServiceRow[]>([]);
  const [newCounter, setNewCounter] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (async () => {
      const { data, error: err } = await supabase
        .from('services')
        .select('id, name, price, duration_minutes, description')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .order('sort_order')
        .order('created_at');
      if (cancelled) return;
      if (err) {
        setError(`Couldn't load services: ${err.message}`);
        setLoading(false);
        return;
      }
      setRows(
        (data ?? []).map(r => ({
          id: r.id,
          isNew: false,
          dirty: false,
          deleted: false,
          name: r.name ?? '',
          price: r.price != null ? String(r.price) : '',
          duration_minutes: r.duration_minutes != null ? String(r.duration_minutes) : '',
          description: r.description ?? '',
        })),
      );
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [businessId]);

  function updateRow(id: string, patch: Partial<ServiceRow>) {
    setRows(prev =>
      prev.map(r => (r.id === id ? { ...r, ...patch, dirty: r.isNew ? r.dirty : true } : r)),
    );
  }

  function addRow() {
    const id = tempId(newCounter);
    setNewCounter(n => n + 1);
    setRows(prev => [
      ...prev,
      {
        id,
        isNew: true,
        dirty: true,
        deleted: false,
        name: '',
        price: '',
        duration_minutes: '',
        description: '',
      },
    ]);
  }

  function deleteRow(id: string) {
    setRows(prev =>
      prev.flatMap(r => {
        if (r.id !== id) return [r];
        if (r.isNew) return [];
        return [{ ...r, deleted: true }];
      }),
    );
  }

  const visibleRows = rows.filter(r => !r.deleted);

  function isRowEmpty(r: ServiceRow): boolean {
    // A row is empty unless it has either a non-blank name OR a positive
    // price. Description alone does not save — a description-only row would
    // get the 'Untitled service' / price=0 fallback and the resolver wouldn't
    // count it, leaving an orphaned ghost row in the DB.
    return !r.name.trim() && !isPositive(canonicalMoney(r.price));
  }

  type RowField = string;
  const validate = (): FieldErrors<RowField> => {
    const e: FieldErrors<RowField> = {};
    for (const r of visibleRows) {
      if (isRowEmpty(r)) continue;
      if (r.price.trim()) {
        if (!isPositive(canonicalMoney(r.price))) e[`price-${r.id}`] = 'Enter a valid price.';
      }
    }
    return e;
  };
  const { canSave: formatOk, showError, handleBlur, markAllTouched } =
    useFieldErrors<RowField>(validate);

  const canSave = !saving && formatOk;

  async function handleSave() {
    if (!formatOk) {
      markAllTouched();
      return;
    }
    setError(null);
    setSaving(true);
    try {
      // Strip rows the user added but never filled in meaningfully. A row
      // counts as empty unless it has a non-blank name or a positive price;
      // description alone is not enough to save (would otherwise create
      // ghost rows the resolver ignores).
      const newRows = rows.filter(r => r.isNew && !r.deleted && !isRowEmpty(r));
      if (newRows.length > 0) {
        const { error: e } = await supabase
          .from('services')
          .insert(
            newRows.map(r => ({
              business_id: businessId,
              name: r.name.trim() || 'Untitled service',
              price: Number(canonicalMoney(r.price)),
              duration_minutes: parseInt(r.duration_minutes) || 60,
              description: r.description.trim() || null,
              is_active: true,
            })),
          );
        if (e) throw e;
      }

      const updateRows = rows.filter(r => !r.isNew && !r.deleted && r.dirty);
      for (const r of updateRows) {
        const { error: e } = await supabase
          .from('services')
          .update({
            name: r.name.trim() || 'Untitled service',
            price: Number(canonicalMoney(r.price)),
            duration_minutes: parseInt(r.duration_minutes) || 60,
            description: r.description.trim() || null,
          })
          .eq('id', r.id);
        if (e) throw e;
      }

      const deleteRows = rows.filter(r => !r.isNew && r.deleted);
      if (deleteRows.length > 0) {
        const { error: e } = await supabase
          .from('services')
          .update({ is_active: false })
          .in('id', deleteRows.map(r => r.id));
        if (e) throw e;
      }
      setSaving(false);
      onSaved();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setError(`Couldn't save services: ${message}`);
      setSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 4px 0' }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '6px 8px 6px 4px',
          marginBottom: 16,
          background: 'transparent',
          border: 'none',
          color: admin.caption,
          fontSize: typography.size.caption,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <ChevronLeft size={16} />
        <span>Setup guide</span>
        <span style={{ color: palette.g400, margin: '0 4px' }}>›</span>
        <span style={{ color: admin.body }}>List your services</span>
      </button>

      <h1
        style={{
          margin: 0,
          fontSize: typography.size.title,
          fontWeight: typography.weight.bold,
          color: admin.header,
          letterSpacing: '-0.01em',
        }}
      >
        List your services
      </h1>
      <p
        style={{
          margin: '8px 0 24px',
          fontSize: typography.size.body,
          color: admin.body,
          lineHeight: typography.lineHeight.relaxed,
          maxWidth: 560,
        }}
      >
        What do you offer clients? This is the menu they see when booking, and these are the line
        items that end up on your invoices.
      </p>

      {loading ? (
        <div style={{ fontSize: typography.size.caption, color: admin.caption }}>Loading…</div>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              marginBottom: 16,
            }}
          >
            {visibleRows.map(row => (
              <ServiceCard
                key={row.id}
                row={row}
                priceError={showError(`price-${row.id}`)}
                onPriceBlur={() => handleBlur(`price-${row.id}`)}
                onChange={patch => updateRow(row.id, patch)}
                onDelete={() => deleteRow(row.id)}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={addRow}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              borderRadius: radii.md,
              border: `1px dashed ${palette.g300}`,
              background: palette.white,
              color: palette.accent,
              fontSize: typography.size.caption,
              fontWeight: typography.weight.semibold,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginBottom: 24,
            }}
          >
            <Plus size={14} />
            Add a service
          </button>

          {error && (
            <div
              style={{
                background: palette.redWash,
                border: `1px solid ${palette.red}`,
                borderRadius: radii.md,
                padding: 12,
                marginBottom: 16,
                color: palette.red,
                fontSize: typography.size.caption,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              style={{
                padding: '10px 20px',
                borderRadius: radii.md,
                border: 'none',
                background: canSave ? palette.accent : palette.g200,
                color: palette.white,
                fontSize: typography.size.body,
                fontWeight: typography.weight.semibold,
                cursor: canSave ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
              }}
            >
              {saving ? 'Saving…' : 'Save services'}
            </button>
            <button
              type="button"
              onClick={onBack}
              disabled={saving}
              style={{
                padding: '10px 16px',
                borderRadius: radii.md,
                border: 'none',
                background: 'transparent',
                color: admin.caption,
                fontSize: typography.size.body,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: radii.sm,
  border: `1px solid ${palette.g200}`,
  fontSize: typography.size.body,
  fontFamily: 'inherit',
  outline: 'none',
  background: palette.white,
};

function ServiceCard({
  row,
  priceError,
  onPriceBlur,
  onChange,
  onDelete,
}: {
  row: ServiceRow;
  priceError: string | null;
  onPriceBlur: () => void;
  onChange: (patch: Partial<ServiceRow>) => void;
  onDelete: () => void;
}) {
  const priceInputStyle: React.CSSProperties = {
    ...inputStyle,
    width: '100%',
    border: priceError ? `1px solid ${palette.red}` : inputStyle.border,
  };
  return (
    <div
      style={{
        background: palette.white,
        border: `1px solid ${palette.g150}`,
        borderRadius: radii.md,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <FieldLabel>Service name</FieldLabel>
          <input
            type="text"
            value={row.name}
            onChange={e => onChange({ name: e.target.value })}
            placeholder="Service name"
            style={{ ...inputStyle, width: '100%' }}
          />
        </div>
        <button
          type="button"
          onClick={onDelete}
          aria-label="Delete service"
          style={{
            marginTop: 22,
            padding: 8,
            background: 'transparent',
            border: 'none',
            color: admin.muted,
            cursor: 'pointer',
            borderRadius: radii.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Trash2 size={16} />
        </button>
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <FieldLabel>Price</FieldLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: admin.caption, fontSize: typography.size.body }}>$</span>
            <input
              type="text"
              inputMode="decimal"
              value={row.price}
              onChange={e => onChange({ price: e.target.value })}
              onBlur={onPriceBlur}
              placeholder="0.00"
              style={priceInputStyle}
            />
          </div>
          {priceError && (
            <div
              style={{
                fontSize: typography.size.micro,
                color: palette.red,
                marginTop: 4,
                fontWeight: typography.weight.medium,
              }}
            >
              {priceError}
            </div>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <FieldLabel>Duration</FieldLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="text"
              inputMode="numeric"
              value={row.duration_minutes}
              onChange={e => onChange({ duration_minutes: e.target.value })}
              placeholder="60"
              style={{ ...inputStyle, width: '100%' }}
            />
            <span style={{ color: admin.caption, fontSize: typography.size.caption }}>min</span>
          </div>
        </div>
      </div>

      <div>
        <FieldLabel>Description (optional)</FieldLabel>
        <input
          type="text"
          value={row.description}
          onChange={e => onChange({ description: e.target.value })}
          placeholder="e.g. what's included, materials, time"
          style={{ ...inputStyle, width: '100%' }}
        />
      </div>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: typography.size.micro,
        fontWeight: typography.weight.semibold,
        color: admin.caption,
        marginBottom: 4,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {children}
    </div>
  );
}
